import { Controller } from "@hotwired/stimulus"
import { EditorView, basicSetup } from "codemirror"
import { EditorState } from "@codemirror/state"
import { json } from "@codemirror/lang-json"
import { oneDark } from "@codemirror/theme-one-dark"

export default class extends Controller {
  static targets = ["editor", "response", "status", "sendButton", "formatButton"]
  static values = {
    url: String,
    csrfToken: String
  }

  connect() {
    console.log("webhook-tester controller connected", this.urlValue)
    this.initEditor()
    this.initResponseViewer()
  }

  disconnect() {
    if (this.editor) this.editor.destroy()
    if (this.responseViewer) this.responseViewer.destroy()
  }

  initEditor() {
    const defaultPayload = JSON.stringify({
      event: "test",
      data: {
        message: "Hello from test webhook",
        timestamp: new Date().toISOString()
      }
    }, null, 2)

    this.editor = new EditorView({
      state: EditorState.create({
        doc: defaultPayload,
        extensions: [
          basicSetup,
          json(),
          oneDark,
          EditorView.theme({
            "&": { height: "300px" },
            ".cm-scroller": { overflow: "auto" }
          })
        ]
      }),
      parent: this.editorTarget
    })
  }

  initResponseViewer() {
    this.responseViewer = new EditorView({
      state: EditorState.create({
        doc: "// Response will appear here after sending a test request",
        extensions: [
          basicSetup,
          json(),
          oneDark,
          EditorState.readOnly.of(true),
          EditorView.theme({
            "&": { height: "300px" },
            ".cm-scroller": { overflow: "auto" }
          })
        ]
      }),
      parent: this.responseTarget
    })
  }

  format() {
    try {
      const content = this.editor.state.doc.toString()
      const parsed = JSON.parse(content)
      const formatted = JSON.stringify(parsed, null, 2)
      this.editor.dispatch({
        changes: { from: 0, to: this.editor.state.doc.length, insert: formatted }
      })
    } catch (e) {
      this.setStatus("error", `Invalid JSON: ${e.message}`)
    }
  }

  clear() {
    this.editor.dispatch({
      changes: { from: 0, to: this.editor.state.doc.length, insert: "" }
    })
  }

  async send(event) {
    console.log("send clicked")
    event.preventDefault()
    
    const content = this.editor.state.doc.toString()
    
    // Validate JSON
    try {
      JSON.parse(content)
    } catch (e) {
      this.setStatus("error", `Invalid JSON: ${e.message}`)
      return
    }

    this.setLoading(true)
    this.setStatus("loading", "Sending request...")
    const startTime = performance.now()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue
        },
        body: JSON.stringify({ payload: content })
      })

      const duration = Math.round(performance.now() - startTime)
      const data = await response.json()

      // Update response viewer
      this.responseViewer.dispatch({
        changes: { 
          from: 0, 
          to: this.responseViewer.state.doc.length, 
          insert: JSON.stringify(data, null, 2) 
        }
      })

      if (response.ok) {
        this.setStatus("success", `${response.status} OK (${duration}ms)`)
      } else {
        this.setStatus("error", `${response.status} ${response.statusText} (${duration}ms)`)
      }
    } catch (e) {
      this.setStatus("error", `Request failed: ${e.message}`)
      this.responseViewer.dispatch({
        changes: { 
          from: 0, 
          to: this.responseViewer.state.doc.length, 
          insert: `// Error: ${e.message}` 
        }
      })
    } finally {
      this.setLoading(false)
    }
  }

  setStatus(type, message) {
    const colors = {
      success: "text-green-400",
      error: "text-red-400",
      loading: "text-yellow-400"
    }
    
    this.statusTarget.className = `text-sm ${colors[type] || "text-gray-400"}`
    this.statusTarget.textContent = message
  }

  setLoading(loading) {
    this.sendButtonTarget.disabled = loading
    if (loading) {
      this.sendButtonTarget.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Sending...
      `
    } else {
      this.sendButtonTarget.innerHTML = `
        <i data-lucide="send" class="w-4 h-4"></i>
        Send Test Request
      `
      // Re-initialize lucide icons
      if (window.lucide) window.lucide.createIcons()
    }
  }
}
